import { IonCard } from '@ionic/react';
import clsx from 'clsx';
import { FC, ReactNode, useEffect } from 'react';
import { useDatasetFormData } from '../../hooks/context/useDatasetFormData';
import { useForceErrorModal } from '../../context/ForceErrorModalContext';
import { useRouteTransitionContext } from '../../hooks/context/useRouteTransitionContext';
import { useTranslate } from '../../hooks/context/useTranslate';
import '../../components/Manager/DatasetForm/DatasetForm.css';
import ApiError from '../../components/ApiError/ApiError';
import OperationCompleted from '../../components/OperationCompleted/OperationCompleted';
import DatasetForm from '../../components/Manager/DatasetForm/DatasetForm';
import { DatasetFormProps } from '../../components/Manager/DatasetForm/types';
import { ReferenceControl } from '../../types/types';
import { useFormTokenChainContext } from '../../hooks/context/useFormTokenChainContext';
import {
  Route,
  Switch,
  useHistory,
  useParams,
  useRouteMatch,
} from 'react-router';
import useRoutes from '../../hooks/useRoutes';
import { getControlWithResolvedFilter } from '../../utils/filters/getControlWithResolvedFilter';
import { getFormReferenceMatchPath } from '../../utils/forms/getFormReferenceMatchPath';
import FormReference from '../Reference/FormReference';
import { getFormReferencePathFromFormMatchPath } from '../../utils/forms/getFormReferencePathFromFormMatchPath';
import DatasetFormDataContextProvider from '../../context/DatasetFormDataContext/DatasetFormDataContext';
import { useFormNavigationGuard } from './hooks/useFormNavigationGuard';
import ResponseActionsScopeProvider from '../../context/ResponseActionsScopeContext';

const FormWrapper: FC<{ children: ReactNode; className?: string }> = ({
  children,
  className,
}) => {
  return (
    <div className={clsx('page-container', className)}>
      <IonCard>{children}</IonCard>
    </div>
  );
};

// Renders outside FormWrapper while loading or on error so no IonCard border is shown.
// Switches to FormWrapper > DatasetForm once data is ready.
const DatasetFormContent: FC<{
  openFullscreenReference: DatasetFormProps['openFullscreenReference'];
}> = ({ openFullscreenReference }) => {
  const {
    isLoading,
    apiError,
    actualToken,
    closeDatasetForm,
    standaloneFormSubmitCompleted,
  } = useDatasetFormData();
  const { isForceModalOpen } = useForceErrorModal();
  const { requestOverlay, releaseOverlay } = useRouteTransitionContext();
  const { t } = useTranslate();

  // Drive the shared PageLayout overlay instead of rendering our own; hidden
  // while ForceErrorModal is the sole visible UI.
  const shouldOverlay = isLoading && !isForceModalOpen;

  useEffect(() => {
    if (!shouldOverlay) {
      return;
    }
    requestOverlay();
    return releaseOverlay;
  }, [shouldOverlay, requestOverlay, releaseOverlay]);

  if (isLoading) {
    return null;
  }

  if (apiError) {
    // Use "back to detail" label when the form was opened from a detail page
    const backLabel = actualToken?.fromUrl?.includes('/detail/')
      ? t('backToDetail')
      : t('backToList');
    return (
      <ApiError
        error={apiError}
        onBack={() => closeDatasetForm()}
        backLabel={backLabel}
      />
    );
  }

  // Standalone menu form submitted — completion screen; confirm leaves via
  // closeDatasetForm (standalone exit goes to the dashboard)
  if (standaloneFormSubmitCompleted) {
    return (
      <OperationCompleted
        title={t('operationCompleted.title')}
        confirmLabel={t('operationCompleted.confirm')}
        onConfirm={() => closeDatasetForm()}
      />
    );
  }

  return (
    <FormWrapper className="form-page-container">
      <DatasetForm openFullscreenReference={openFullscreenReference} />
    </FormWrapper>
  );
};

const Form: FC = () => {
  const match = useRouteMatch();
  const routes = useRoutes();
  const { datasetId, formId, sourceItemId, itemId, subsetId, subsetItemId } =
    useParams<{
      datasetId: string;
      formId: string;
      sourceItemId?: string;
      itemId?: string;
      subsetId?: string;
      subsetItemId?: string;
    }>();
  const history = useHistory();
  const { createNestedToken, getActualTokenFromUrl } =
    useFormTokenChainContext();

  useFormNavigationGuard();

  const referenceMatchPath = getFormReferenceMatchPath(match.path, routes);

  const handleOpenFullscreenReference = (
    name: string,
    referenceDatasetId: string,
    control: ReferenceControl,
    value: string,
    label: string,
    // resolvedFilter: pre-resolved control.filter; null = resolved to nothing (strip filter)
    resolvedFilter?: Record<string, any> | null
  ) => {
    const effectiveControl = getControlWithResolvedFilter(
      control,
      resolvedFilter
    );
    const fullscreenReferenceObject = {
      name,
      datasetId: referenceDatasetId,
      control: effectiveControl,
      value: value || '',
      label,
      isFormInput: true,
    };
    const actualToken = getActualTokenFromUrl();

    if (!actualToken) {
      throw new Error(
        'Unable to find actual token to open fullscreen reference'
      );
    }

    const newUrl = getFormReferencePathFromFormMatchPath(
      match.path,
      routes,
      datasetId,
      formId,
      referenceDatasetId,
      name,
      sourceItemId,
      itemId,
      subsetId,
      subsetItemId
    );

    if (!newUrl) {
      throw new Error('Unable to generate new URL for fullscreen reference');
    }

    const hasMaster = actualToken.meta?.requestData?.['master'] !== undefined;

    const childToken = createNestedToken(actualToken.id, {
      url: newUrl,
      values: fullscreenReferenceObject,
      meta: {
        ...(hasMaster
          ? {
              requestData: {
                master: {
                  id: actualToken.meta?.requestData?.['master'].id,
                  idItem: actualToken.meta?.requestData?.['master'].idItem,
                },
              },
            }
          : {}),
      },
    });

    history.replace(newUrl + '?token=' + childToken.id, {
      redirectedFromForm: true,
    });
  };

  const DatasetFormPage = (
    <ResponseActionsScopeProvider scope="form">
      <DatasetFormDataContextProvider key={formId}>
        <DatasetFormContent
          openFullscreenReference={handleOpenFullscreenReference}
        />
      </DatasetFormDataContextProvider>
    </ResponseActionsScopeProvider>
  );

  const ReferencePage = (
    <FormWrapper>
      <FormReference />
    </FormWrapper>
  );

  return (
    <Switch>
      <Route path={referenceMatchPath}>{ReferencePage}</Route>
      <Route path={match.path} exact>
        {DatasetFormPage}
      </Route>
    </Switch>
  );
};

export default Form;
